import { useEffect, useCallback } from 'react';

/**
 * Хук для обработчиков событий
 */
export const useEventHandlers = (
  tabId,
  currentVehicle,
  setCurrentVehicle,
  currentDateRange,
  setCurrentDateRange,
  mapInitialized,
  fetchTrackData,
  ensureValidDate,
  saveVehicleToLocalStorage,
  saveDateRangeToLocalStorage,
  refreshMapView
) => {

  /**
   * Обработчик принудительного обновления (копия из TrackMap)
   * @param {Object} event - Событие принудительного обновления
   */
  const handleForceUpdate = useCallback((event) => {
    try {
      console.log('TrackReport: Получено событие принудительного обновления:', event.detail);
      
      const { 
        forceVehicle, 
        forceStartDate, 
        forceEndDate, 
        targetTabId 
      } = event.detail;
      
      // Проверяем, предназначено ли событие для этого таба
      if (targetTabId && targetTabId !== tabId) {
        console.log('TrackReport: Событие не для этого таба, игнорируем');
        return;
      }
      
      let hasChanges = false;
      
      // Обновляем транспорт если передан
      if (forceVehicle && forceVehicle.id !== currentVehicle?.id) {
        console.log('TrackReport: Принудительное обновление транспорта:', forceVehicle.name);
        setCurrentVehicle(forceVehicle);
        saveVehicleToLocalStorage(forceVehicle);
        hasChanges = true;
      }
      
      // Обновляем даты если переданы
      if (forceStartDate || forceEndDate) {
        const newStart = ensureValidDate(forceStartDate);
        const newEnd = ensureValidDate(forceEndDate);
        
        if (newStart && newEnd) {
          console.log('TrackReport: Принудительное обновление дат:', 
            newStart.toLocaleDateString(), '-', newEnd.toLocaleDateString());
          
          // Проверяем, отличаются ли новые даты от текущих
          const startDiff = Math.abs(currentDateRange.startDate?.getTime() - newStart.getTime()) > 1000;
          const endDiff = Math.abs(currentDateRange.endDate?.getTime() - newEnd.getTime()) > 1000;
          
          if (startDiff || endDiff) {
            const newDateRange = { startDate: newStart, endDate: newEnd };
            setCurrentDateRange(newDateRange);
            saveDateRangeToLocalStorage(newDateRange);
            hasChanges = true;
          }
        }
      }
      
      // Если есть изменения и карта инициализирована, загружаем новые данные
      if (hasChanges && mapInitialized && currentVehicle) {
        setTimeout(() => {
          fetchTrackData(
            forceVehicle || currentVehicle,
            { 
              startDate: ensureValidDate(forceStartDate) || currentDateRange.startDate,
              endDate: ensureValidDate(forceEndDate) || currentDateRange.endDate
            }
          );
        }, 300);
      }
      
    } catch (error) {
      console.error('TrackReport: Ошибка при принудительном обновлении:', error);
    }
  }, [
    tabId, currentVehicle, setCurrentVehicle, currentDateRange, setCurrentDateRange,
    mapInitialized, fetchTrackData, ensureValidDate, saveVehicleToLocalStorage, saveDateRangeToLocalStorage
  ]);

  /**
   * Обработчик изменения localStorage (копия из TrackMap)
   * @param {Object} event - Событие изменения localStorage
   */
  const handleStorageChange = useCallback((event) => {
    try {
      if (!event.newValue) return;
      
      const now = new Date().getTime();
      
      // Обработка изменения диапазона дат
      if (event.key === 'lastDateRange') {
        try {
          const newRange = JSON.parse(event.newValue);
          
          if (newRange && newRange.startDate && newRange.endDate) {
            // Проверяем timestamp для избежания зацикливания
            if (newRange.timestamp && now - newRange.timestamp < 2000) {
              console.log('TrackReport: Пропуск недавнего изменения диапазона дат из localStorage');
              return;
            }
            
            const newStartDate = ensureValidDate(newRange.startDate);
            const newEndDate = ensureValidDate(newRange.endDate);
            
            if (newStartDate && newEndDate) {
              console.log('TrackReport: Обновление диапазона дат из localStorage');
              
              const newDateRange = { startDate: newStartDate, endDate: newEndDate };
              setCurrentDateRange(newDateRange);
              
              // Загружаем новые данные если есть транспорт и карта инициализирована
              if (currentVehicle && mapInitialized) {
                setTimeout(() => fetchTrackData(currentVehicle, newDateRange), 300);
              }
            }
          }
        } catch (parseError) {
          console.error('TrackReport: Ошибка парсинга диапазона дат из localStorage:', parseError);
        }
      }
      
      // Обработка изменения транспорта
      if (event.key === 'lastSelectedVehicle') {
        try {
          const newVehicle = JSON.parse(event.newValue);
          
          if (newVehicle && newVehicle.id) {
            // Проверяем timestamp для избежания зацикливания
            if (newVehicle._updateTimestamp && now - newVehicle._updateTimestamp < 2000) {
              console.log('TrackReport: Пропуск недавнего изменения транспорта из localStorage');
              return;
            }
            
            if (newVehicle.id !== currentVehicle?.id) {
              console.log('TrackReport: Обновление транспорта из localStorage:', newVehicle.name);
              setCurrentVehicle(newVehicle);
              
              // Загружаем новые данные если карта инициализирована и есть даты
              if (mapInitialized && currentDateRange.startDate && currentDateRange.endDate) {
                setTimeout(() => fetchTrackData(newVehicle, currentDateRange), 300);
              }
            }
          }
        } catch (parseError) {
          console.error('TrackReport: Ошибка парсинга транспорта из localStorage:', parseError);
        }
      }
      
    } catch (error) {
      console.error('TrackReport: Ошибка при обработке изменения localStorage:', error);
    }
  }, [
    currentVehicle, setCurrentVehicle, currentDateRange, setCurrentDateRange,
    mapInitialized, fetchTrackData, ensureValidDate
  ]);

  /**
   * Обработчик изменения диапазона дат (копия из TrackMap)
   * @param {Object} event - Событие изменения диапазона дат
   */
  const handleDateRangeChanged = useCallback((event) => {
    try {
      console.log('TrackReport: Получено событие изменения диапазона дат:', event.detail);
      
      const { startDate, endDate, targetTabId } = event.detail;
      const now = new Date().getTime();
      
      // Проверяем, предназначено ли событие для этого таба
      if (targetTabId && targetTabId !== tabId) {
        console.log('TrackReport: Событие изменения дат не для этого таба, игнорируем');
        return;
      }
      
      // Дебаунсинг для предотвращения частых обновлений
      if (window.lastDateRangeUpdate && now - window.lastDateRangeUpdate < 1000) {
        console.log('TrackReport: Слишком частое изменение дат, пропускаем');
        return;
      }
      window.lastDateRangeUpdate = now;
      
      const validStartDate = ensureValidDate(startDate);
      const validEndDate = ensureValidDate(endDate);
      
      if (validStartDate && validEndDate) {
        console.log('TrackReport: Применение нового диапазона дат:', 
          validStartDate.toLocaleDateString(), '-', validEndDate.toLocaleDateString());
        
        const newDateRange = { startDate: validStartDate, endDate: validEndDate };
        setCurrentDateRange(newDateRange);
        saveDateRangeToLocalStorage(newDateRange, now);
        
        // Загружаем новые данные если есть транспорт и карта инициализирована
        if (currentVehicle && mapInitialized) {
          setTimeout(() => fetchTrackData(currentVehicle, newDateRange), 300);
        }
      } else {
        console.warn('TrackReport: Некорректные даты в событии изменения диапазона');
      }
      
    } catch (error) {
      console.error('TrackReport: Ошибка при обработке изменения диапазона дат:', error);
    }
  }, [
    tabId, currentVehicle, mapInitialized, fetchTrackData, 
    setCurrentDateRange, ensureValidDate, saveDateRangeToLocalStorage
  ]);

  /**
   * Обработчик выбора транспорта (копия из TrackMap)
   * @param {Object} event - Событие выбора транспорта
   */
  const handleVehicleSelected = useCallback((event) => {
    try {
      console.log('TrackReport: Получено событие выбора транспорта:', event.detail);
      
      const { vehicle: selectedVehicle, targetTabId } = event.detail;
      
      // Проверяем, предназначено ли событие для этого таба
      if (targetTabId && targetTabId !== tabId) {
        console.log('TrackReport: Событие выбора транспорта не для этого таба, игнорируем');
        return;
      }
      
      if (selectedVehicle && selectedVehicle.id !== currentVehicle?.id) {
        console.log('TrackReport: Применение выбранного транспорта:', selectedVehicle.name);
        setCurrentVehicle(selectedVehicle);
        saveVehicleToLocalStorage(selectedVehicle);
        
        // Загружаем новые данные если карта инициализирована и есть даты
        if (mapInitialized && currentDateRange.startDate && currentDateRange.endDate) {
          setTimeout(() => fetchTrackData(selectedVehicle, currentDateRange), 300);
        }
      }
      
    } catch (error) {
      console.error('TrackReport: Ошибка при обработке выбора транспорта:', error);
    }
  }, [
    tabId, currentVehicle, setCurrentVehicle, mapInitialized, 
    currentDateRange, fetchTrackData, saveVehicleToLocalStorage
  ]);

  /**
   * Обработчики событий для табов (копия из TrackMap)
   * @param {Object} event - Событие выбора транспорта для таба
   */
  const handleVehicleSelectedInTab = useCallback((event) => {
    try {
      const { vehicle: newVehicle, targetTabId } = event.detail;
      
      if (targetTabId === tabId && newVehicle) {
        console.log('TrackReport: Выбрано ТС для таба:', tabId, newVehicle.name);
        setCurrentVehicle(newVehicle);
        saveVehicleToLocalStorage(newVehicle);
        
        if (mapInitialized && currentDateRange.startDate && currentDateRange.endDate) {
          setTimeout(() => fetchTrackData(newVehicle, currentDateRange), 300);
        }
      }
    } catch (error) {
      console.error('TrackReport: Ошибка при обработке выбора ТС в табе:', error);
    }
  }, [tabId, mapInitialized, currentDateRange, fetchTrackData, setCurrentVehicle, saveVehicleToLocalStorage]);

  /**
   * Обработчики для изменения дат в табе (копия из TrackMap)
   * @param {Object} event - Событие изменения дат для таба
   */
  const handleDateRangeChangedInTab = useCallback((event) => {
    try {
      const { startDate, endDate, targetTabId } = event.detail;
      
      if (targetTabId === tabId && startDate && endDate) {
        console.log('TrackReport: Изменены даты для таба:', tabId);
        const validStartDate = ensureValidDate(startDate);
        const validEndDate = ensureValidDate(endDate);
        
        if (validStartDate && validEndDate) {
          const newDateRange = { startDate: validStartDate, endDate: validEndDate };
          setCurrentDateRange(newDateRange);
          saveDateRangeToLocalStorage(newDateRange);
          
          if (currentVehicle && mapInitialized) {
            setTimeout(() => fetchTrackData(currentVehicle, newDateRange), 300);
          }
        }
      }
    } catch (error) {
      console.error('TrackReport: Ошибка при обработке изменения дат в табе:', error);
    }
  }, [
    tabId, currentVehicle, mapInitialized, fetchTrackData, 
    ensureValidDate, setCurrentDateRange, saveDateRangeToLocalStorage
  ]);

  /**
   * Обработчики для изменения размера и видимости (копия из TrackMap)
   */
  const handleResize = useCallback(() => {
    if (document.hidden) return;
    console.log('TrackReport: Изменение размера окна');
    refreshMapView();
  }, [refreshMapView]);

  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'visible') {
      console.log('TrackReport: Страница стала видимой');
      setTimeout(refreshMapView, 300);
    }
  }, [refreshMapView]);

  // Регистрация всех обработчиков событий
  useEffect(() => {
    if (!tabId) return;

    console.log('TrackReport: Регистрация обработчиков событий для таба:', tabId);

    // Глобальные события
    document.addEventListener('forceUpdate', handleForceUpdate);
    window.addEventListener('storage', handleStorageChange);
    document.addEventListener('dateRangeChanged', handleDateRangeChanged);
    document.addEventListener('vehicleSelected', handleVehicleSelected);
    
    // События для табов
    document.addEventListener('vehicleSelectedInTab', handleVehicleSelectedInTab);
    document.addEventListener('dateRangeChangedInTab', handleDateRangeChangedInTab);
    
    // События окна
    window.addEventListener('resize', handleResize);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      console.log('TrackReport: Отмена регистрации обработчиков для таба:', tabId);
      
      // Удаляем все обработчики
      document.removeEventListener('forceUpdate', handleForceUpdate);
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('dateRangeChanged', handleDateRangeChanged);
      document.removeEventListener('vehicleSelected', handleVehicleSelected);
      document.removeEventListener('vehicleSelectedInTab', handleVehicleSelectedInTab);
      document.removeEventListener('dateRangeChangedInTab', handleDateRangeChangedInTab);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [
    tabId, handleForceUpdate, handleStorageChange, handleDateRangeChanged, 
    handleVehicleSelected, handleVehicleSelectedInTab, handleDateRangeChangedInTab,
    handleResize, handleVisibilityChange
  ]);

  return {
    // Функции обработки событий (для внешнего использования при необходимости)
    handleForceUpdate,
    handleStorageChange,
    handleDateRangeChanged,
    handleVehicleSelected,
    handleVehicleSelectedInTab,
    handleDateRangeChangedInTab,
    handleResize,
    handleVisibilityChange
  };
}; 